import { isCustomLeagueKey } from "@sportsmet/contest-engine";
import type { ReactNode } from "react";

import { PickemCreateReviewRules } from "../pickem/PickemCreateSetupStep";
import { SquaresCreateReviewSummary } from "../squares/SquaresCreateReviewSummary";
import { VISIBILITY_ITEMS } from "./CreatePoolBasicsStep";
import type {
  typeCreatePoolDraft,
  typeCreatePoolLeagueOption,
} from "./createPoolDraft";
import { stripCardSetupReviewRows } from "../strips/stripCardCreateSetupDraft";
import { CreatePoolReviewRulesText } from "./CreatePoolReviewRulesText";

export type typeCreatePoolReviewStepProps = {
  draft: typeCreatePoolDraft;
  leagueOptions: readonly typeCreatePoolLeagueOption[];
  contestTypeLabel: string;
};

/** Step 4: lists every entered value before submission. */
export const CreatePoolReviewStep = (
  props: typeCreatePoolReviewStepProps,
): ReactNode => {
  const league = props.leagueOptions.find(
    (option) => option.leagueKey === props.draft.leagueKey,
  );
  const visibility = VISIBILITY_ITEMS.find(
    (item) => item.value === props.draft.visibility,
  );

  const rows: { dt: string; dd: string }[] = [
    { dt: "League", dd: league?.label ?? "" },
    { dt: "Contest", dd: props.contestTypeLabel },
    // ISSUE-1092. The season the server will store, as the catalog names it.
    // ISSUE-1299. A pool with no league has no season to name.
    ...(isCustomLeagueKey(props.draft.leagueKey)
      ? []
      : [{ dt: "Season", dd: league?.seasonLabel ?? "" }]),
    { dt: "Pool name", dd: props.draft.name.trim() },
    {
      dt: "Approve new members myself",
      dd: props.draft.autoApprove ? "Off" : "On",
    },
    {
      dt: "Let members invite others",
      dd: props.draft.memberSharingEnabled ? "On" : "Off",
    },
    { dt: "Chat", dd: props.draft.chatEnabled ? "On" : "Off" },
    { dt: "Who can find this pool", dd: visibility?.label ?? "" },
    ...(props.draft.contestType === "stripCard"
      ? stripCardSetupReviewRows(props.draft.typeSetups.stripCard)
      : []),
  ];

  return (
    <div className="flex flex-col gap-6">
      <dl className="flex flex-col gap-3" data-testid="create-pool-step-review">
        {rows.map((row) => (
          <div key={row.dt}>
            <dt className="text-caption text-ink-secondary">{row.dt}</dt>
            <dd className="text-body-strong text-ink">{row.dd}</dd>
          </div>
        ))}
      </dl>
      {/* ISSUE-1258. Survivor's rules text reads Survivor rules only; the
          other types' setup steps own their own review in a later card. */}
      {props.draft.contestType === "survivor" ? (
        <CreatePoolReviewRulesText draft={props.draft} />
      ) : null}
      {/* ISSUE-1269. Squares restates its setup before Create pool. */}
      {props.draft.contestType === "squares" ? (
        <SquaresCreateReviewSummary
          leagueKey={props.draft.leagueKey}
          draft={props.draft.typeSetups.squares}
        />
      ) : null}
      {/* ISSUE-1303. Pick'em's rules text shows here too, from the server. */}
      {props.draft.contestType === "pickem" ? (
        <PickemCreateReviewRules
          leagueKey={props.draft.leagueKey}
          setup={props.draft.typeSetups.pickem}
        />
      ) : null}
    </div>
  );
};
